import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, HeartHandshake, RotateCw } from "lucide-react";

const SUGGESTIONS = [
  "Preciso lembrar da consulta do meu pai amanhã.",
  "Minha mãe está esquecendo de tomar o remédio da tarde. Como faço?",
  "Como organizo os exames pendentes da semana?",
  "Meu pai comeu pouco hoje. Isso é preocupante?",
];

export default function FPAssistenteIA() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    // Optimistic push
    setMessages((m) => [...m, { role: "user", content: msg, at: new Date().toISOString() }]);
    try {
      const { data } = await api.post("/fp/ai/chat", {
        message: msg, session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sofia está offline no momento");
    } finally { setSending(false); }
  };

  const reset = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-ai">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-700 font-bold">
            <HeartHandshake className="w-3.5 h-3.5" /> Filho Presente · Sofia
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62] mt-2">
            Sua assistente de cuidado
          </h1>
          <p className="text-slate-600 text-sm mt-1 max-w-xl">
            Converse com a Sofia sobre a rotina do idoso, lembretes, exames e consultas.
            Ela organiza, ajuda a decidir os próximos passos e nunca substitui o médico.
          </p>
        </div>
        {messages.length > 0 && (
          <button onClick={reset} data-testid="fp-ai-reset"
            className="text-xs text-slate-500 hover:text-[#0B2B62] inline-flex items-center gap-1">
            <RotateCw className="w-3.5 h-3.5" /> Nova conversa
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col h-[62vh]">
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <Sparkles className="w-7 h-7 text-amber-600" />
              </div>
              <div className="font-semibold text-[#0B2B62] mt-3">Oi! Eu sou a Sofia 💛</div>
              <div className="text-sm text-slate-600 mt-1">Como posso te ajudar hoje?</div>
              <div className="grid sm:grid-cols-2 gap-2 mt-6">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} data-testid={`fp-ai-suggest-${i}`}
                    className="text-left text-sm bg-slate-50 hover:bg-amber-50 border border-slate-200 hover:border-amber-300 rounded-lg p-3 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`fp-ai-msg-${i}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                m.role === "user"
                  ? "bg-[#0B2B62] text-white rounded-br-sm"
                  : "bg-amber-50 text-slate-800 border border-amber-100 rounded-bl-sm"
              }`}>
                {m.role === "assistant" && (
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1">Sofia</div>
                )}
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-amber-50 border border-amber-100 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-slate-500 inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Sofia está digitando…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-slate-200 p-3 sm:p-4 flex items-center gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)}
            data-testid="fp-ai-input"
            placeholder="Escreva sua mensagem…"
            className="flex-1 px-4 py-2.5 border border-slate-300 rounded-full text-sm outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
          <button type="submit" disabled={!input.trim() || sending}
            data-testid="fp-ai-send"
            className="inline-flex items-center gap-1 bg-[#0B2B62] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
            <Send className="w-4 h-4" /> Enviar
          </button>
        </form>
      </div>

      <div className="text-[11px] text-slate-400 mt-3 text-center leading-relaxed">
        Em emergências (dor no peito, queda com trauma, AVC, desmaio) ligue <b>192 (SAMU)</b>.
        A Sofia não substitui médico.
      </div>
    </div>
  );
}
